import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Card, CardContent, Typography, Button, Chip, IconButton,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField,
  MenuItem, Tooltip, CircularProgress, Grid, ToggleButton,
  ToggleButtonGroup, Divider,
} from '@mui/material';
import {
  Add, Delete, Edit, ArrowBackIos, ArrowForwardIos,
  CalendarMonth, Event, Refresh, Close,
} from '@mui/icons-material';
import { InvokeApi, apiList } from '../../services/ApiService';
import { Config } from '../../config/Config';
import dayjs from 'dayjs';

const EVENT_TYPES = [
  { value: 'Holiday',    label: 'Holiday',    color: '#c62828', bg: '#ffebee' },
  { value: 'Exam',       label: 'Exam',       color: '#1565c0', bg: '#e3f2fd' },
  { value: 'Program',    label: 'Program',    color: '#6a1b9a', bg: '#f3e5f5' },
  { value: 'Sports',     label: 'Sports',     color: '#2e7d32', bg: '#e8f5e9' },
  { value: 'Meeting',    label: 'Meeting',    color: '#e65100', bg: '#fff3e0' },
  { value: 'Other',      label: 'Other',      color: '#37474f', bg: '#eceff1' },
];

const getType = (type) => EVENT_TYPES.find(t => t.value === type) || EVENT_TYPES[5];

const MONTHS = ['January','February','March','April','May','June','July','August','September','October','November','December'];

const EMPTY_FORM = {
  title: '', description: '', event_date: '', end_date: '',
  event_type: 'Holiday', is_holiday: false, ac_year: '',
};

export default function AcademicCalendar() {
  const [acYears, setAcYears]       = useState([]);
  const [selectedYear, setSelectedYear] = useState('');
  const [events, setEvents]         = useState([]);
  const [loading, setLoading]       = useState(false);
  const [viewMode, setViewMode]     = useState('month');   // 'month' | 'list'
  const [currentMonth, setCurrentMonth] = useState(dayjs());
  const [filterType, setFilterType] = useState('');
  const [dialog, setDialog]         = useState(false);
  const [editingId, setEditingId]   = useState(null);
  const [saving, setSaving]         = useState(false);
  const [form, setForm]             = useState(EMPTY_FORM);

  // Load academic years
  useEffect(() => {
    (async () => {
      const data = await InvokeApi(Config.main_url + apiList.getAvailableAcademicYears, {});
      const years = data?.academic_years || [];
      setAcYears(years);
      const current = years.find(y => y.is_current) || years[0];
      if (current) setSelectedYear(current.value);
    })();
  }, []);

  // Load events
  const load = useCallback(async () => {
    if (!selectedYear) return;
    setLoading(true);
    const data = await InvokeApi(Config.main_url + apiList.getCalendarEvents, {
      ac_year: selectedYear,
      event_type: filterType || undefined,
    });
    setEvents(data?.events || []);
    setLoading(false);
  }, [selectedYear, filterType]);

  useEffect(() => { load(); }, [load]);

  // ── Dialog helpers ────────────────────────────────────────────────────────
  const openAdd = (date = '') => {
    setForm({ ...EMPTY_FORM, ac_year: selectedYear, event_date: date, end_date: date });
    setEditingId(null);
    setDialog(true);
  };

  const openEdit = (ev) => {
    setForm({
      title: ev.title, description: ev.description || '',
      event_date: ev.event_date, end_date: ev.end_date || ev.event_date,
      event_type: ev.event_type, is_holiday: !!ev.is_holiday,
      ac_year: ev.ac_year,
    });
    setEditingId(ev.id);
    setDialog(true);
  };

  const handleSave = async () => {
    if (!form.title || !form.event_date || !form.event_type) {
      alert('Title, date and type are required'); return;
    }
    setSaving(true);
    const endpoint = editingId ? apiList.editCalendarEvent : apiList.addCalendarEvent;
    const payload  = editingId ? { ...form, id: editingId } : { ...form, ac_year: selectedYear };
    const data     = await InvokeApi(Config.main_url + endpoint, payload);
    setSaving(false);
    if (data?.code === '200') { setDialog(false); load(); }
    else alert(data?.message || 'Failed');
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this event?')) return;
    const data = await InvokeApi(Config.main_url + apiList.deleteCalendarEvent, { id });
    if (data?.code === '200') load();
    else alert(data?.message || 'Failed to delete');
  };

  const f = (k) => (e) => setForm(p => ({ ...p, [k]: e.target.type === 'checkbox' ? e.target.checked : e.target.value }));

  // ── Calendar grid helpers ─────────────────────────────────────────────────
  const monthStart   = currentMonth.startOf('month');
  const monthEnd     = currentMonth.endOf('month');
  const startPad     = monthStart.day(); // 0=Sun
  const daysInMonth  = monthEnd.date();

  const eventsInMonth = events.filter(ev => {
    const d = dayjs(ev.event_date);
    return d.year() === currentMonth.year() && d.month() === currentMonth.month();
  });

  const eventsByDate = eventsInMonth.reduce((acc, ev) => {
    const key = dayjs(ev.event_date).date();
    acc[key] = acc[key] || [];
    acc[key].push(ev);
    return acc;
  }, {});

  return (
    <Box>
      {/* ── Header ── */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography variant="h5" fontWeight={800} color="#1a237e">Academic Calendar</Typography>
          <Typography variant="body2" color="text.secondary">
            {events.length} events · {selectedYear}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
          {/* Academic year selector */}
          <TextField select size="small" value={selectedYear} onChange={e => setSelectedYear(e.target.value)}
            sx={{ minWidth: 120 }} label="Year">
            {acYears.map(y => <MenuItem key={y.value} value={y.value}>{y.label || y.value}</MenuItem>)}
          </TextField>
          {/* View toggle */}
          <ToggleButtonGroup size="small" value={viewMode} exclusive onChange={(_, v) => v && setViewMode(v)}>
            <ToggleButton value="month" sx={{ fontWeight: 600, px: 2 }}>Month</ToggleButton>
            <ToggleButton value="list"  sx={{ fontWeight: 600, px: 2 }}>List</ToggleButton>
          </ToggleButtonGroup>
          <Tooltip title="Refresh"><IconButton onClick={load} sx={{ border: '1px solid #e8eaf6', borderRadius: 2, color: '#1a237e' }}><Refresh /></IconButton></Tooltip>
          <Button variant="contained" startIcon={<Add />} onClick={() => openAdd()}
            sx={{ background: 'linear-gradient(135deg,#1a237e,#1565c0)', borderRadius: 2, fontWeight: 700 }}>
            Add Event
          </Button>
        </Box>
      </Box>

      {/* ── Type filter chips ── */}
      <Box sx={{ display: 'flex', gap: 1, mb: 2.5, flexWrap: 'wrap' }}>
        <Chip label="All" onClick={() => setFilterType('')}
          sx={{ fontWeight: 700, background: !filterType ? '#1a237e' : '#f5f6fa', color: !filterType ? '#fff' : '#1a237e', cursor: 'pointer' }} />
        {EVENT_TYPES.map(t => (
          <Chip key={t.value} label={t.label} onClick={() => setFilterType(filterType === t.value ? '' : t.value)}
            sx={{ fontWeight: 700, background: filterType === t.value ? t.bg : '#f5f6fa',
              color: filterType === t.value ? t.color : '#666', border: filterType === t.value ? `1.5px solid ${t.color}` : 'none', cursor: 'pointer' }} />
        ))}
      </Box>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 8 }}><CircularProgress sx={{ color: '#1a237e' }} /></Box>
      ) : viewMode === 'month' ? (
        // ── MONTH VIEW ──
        <Card sx={{ borderRadius: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.08)' }}>
          <CardContent sx={{ p: 2 }}>
            {/* Month nav */}
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
              <IconButton onClick={() => setCurrentMonth(m => m.subtract(1, 'month'))} size="small"><ArrowBackIos fontSize="small" /></IconButton>
              <Typography fontWeight={800} fontSize={18} color="#1a237e">
                {MONTHS[currentMonth.month()]} {currentMonth.year()}
              </Typography>
              <IconButton onClick={() => setCurrentMonth(m => m.add(1, 'month'))} size="small"><ArrowForwardIos fontSize="small" /></IconButton>
            </Box>

            {/* Day headers */}
            <Grid container columns={7} sx={{ mb: 0.5 }}>
              {['Sun','Mon','Tue','Wed','Thu','Fri','Sat'].map(d => (
                <Grid item xs={1} key={d}>
                  <Typography fontSize={12} fontWeight={700} color="#1a237e" textAlign="center">{d}</Typography>
                </Grid>
              ))}
            </Grid>

            {/* Calendar cells */}
            <Grid container columns={7}>
              {/* Padding cells */}
              {Array.from({ length: startPad }).map((_, i) => (
                <Grid item xs={1} key={`pad-${i}`}>
                  <Box sx={{ minHeight: 72, p: 0.5 }} />
                </Grid>
              ))}
              {/* Day cells */}
              {Array.from({ length: daysInMonth }).map((_, i) => {
                const day      = i + 1;
                const dayEvents = eventsByDate[day] || [];
                const isToday  = dayjs().date() === day && dayjs().month() === currentMonth.month() && dayjs().year() === currentMonth.year();
                return (
                  <Grid item xs={1} key={day}>
                    <Box onClick={() => openAdd(`${currentMonth.format('YYYY-MM')}-${String(day).padStart(2, '0')}`)}
                      sx={{ minHeight: 72, p: 0.5, border: '1px solid #f0f0f0', borderRadius: 1, cursor: 'pointer',
                        background: isToday ? '#e8eaf6' : '#fff',
                        '&:hover': { background: '#f5f6fa' },
                        position: 'relative' }}>
                      <Typography fontSize={12} fontWeight={isToday ? 800 : 500}
                        color={isToday ? '#1a237e' : 'text.primary'}
                        sx={{ width: 22, height: 22, borderRadius: '50%',
                          background: isToday ? '#1a237e' : 'transparent',
                          color: isToday ? '#fff' : 'inherit',
                          display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 0.3 }}>
                        {day}
                      </Typography>
                      {dayEvents.slice(0, 2).map(ev => {
                        const t = getType(ev.event_type);
                        return (
                          <Box key={ev.id} onClick={e => { e.stopPropagation(); openEdit(ev); }}
                            sx={{ px: 0.5, py: 0.2, mb: 0.3, borderRadius: 0.8,
                              background: t.bg, borderLeft: `3px solid ${t.color}`, cursor: 'pointer' }}>
                            <Typography fontSize={9} fontWeight={700} color={t.color} noWrap>{ev.title}</Typography>
                          </Box>
                        );
                      })}
                      {dayEvents.length > 2 && (
                        <Typography fontSize={9} color="text.disabled">+{dayEvents.length - 2} more</Typography>
                      )}
                    </Box>
                  </Grid>
                );
              })}
            </Grid>
          </CardContent>
        </Card>
      ) : (
        // ── LIST VIEW ──
        <Box>
          {events.length === 0 ? (
            <Card sx={{ borderRadius: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.08)', p: 4, textAlign: 'center' }}>
              <CalendarMonth sx={{ fontSize: 52, color: '#e8eaf6', mb: 1 }} />
              <Typography color="text.secondary" fontWeight={500}>No events for {selectedYear}</Typography>
              <Button variant="contained" startIcon={<Add />} onClick={() => openAdd()} sx={{ mt: 2, background: 'linear-gradient(135deg,#1a237e,#1565c0)', borderRadius: 2, fontWeight: 700 }}>
                Add First Event
              </Button>
            </Card>
          ) : (
            (() => {
              // Group by month
              const grouped = events.reduce((acc, ev) => {
                const key = dayjs(ev.event_date).format('YYYY-MM');
                acc[key] = acc[key] || [];
                acc[key].push(ev);
                return acc;
              }, {});
              return Object.entries(grouped).map(([monthKey, monthEvents]) => (
                <Box key={monthKey} mb={3}>
                  <Typography fontWeight={800} fontSize={15} color="#1a237e" mb={1.5}>
                    {MONTHS[parseInt(monthKey.split('-')[1]) - 1]} {monthKey.split('-')[0]}
                  </Typography>
                  <Card sx={{ borderRadius: 3, boxShadow: '0 2px 12px rgba(0,0,0,0.06)' }}>
                    {monthEvents.map((ev, idx) => {
                      const t = getType(ev.event_type);
                      return (
                        <Box key={ev.id}>
                          {idx > 0 && <Divider />}
                          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, p: 2,
                            borderLeft: `4px solid ${t.color}`, '&:hover': { background: '#fafbff' } }}>
                            {/* Date box */}
                            <Box sx={{ minWidth: 44, textAlign: 'center', background: t.bg, borderRadius: 2, p: 0.8, flexShrink: 0 }}>
                              <Typography fontSize={18} fontWeight={900} color={t.color} lineHeight={1}>
                                {dayjs(ev.event_date).format('DD')}
                              </Typography>
                              <Typography fontSize={10} fontWeight={700} color={t.color}>
                                {dayjs(ev.event_date).format('MMM')}
                              </Typography>
                            </Box>
                            {/* Content */}
                            <Box flex={1}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                                <Typography fontWeight={700} fontSize={14}>{ev.title}</Typography>
                                <Chip label={ev.event_type} size="small"
                                  sx={{ background: t.bg, color: t.color, fontWeight: 700, fontSize: 10 }} />
                                {ev.is_holiday == 1 && (
                                  <Chip label="Holiday" size="small" sx={{ background: '#ffebee', color: '#c62828', fontWeight: 700, fontSize: 10 }} />
                                )}
                              </Box>
                              {ev.description && (
                                <Typography fontSize={12} color="text.secondary" mt={0.3}>{ev.description}</Typography>
                              )}
                              {ev.end_date && ev.end_date !== ev.event_date && (
                                <Typography fontSize={11} color="text.disabled" mt={0.3}>
                                  Until {dayjs(ev.end_date).format('DD MMM YYYY')}
                                </Typography>
                              )}
                            </Box>
                            {/* Actions */}
                            <Box sx={{ display: 'flex', gap: 0.5 }}>
                              <Tooltip title="Edit">
                                <IconButton size="small" onClick={() => openEdit(ev)}
                                  sx={{ color: '#1a237e', '&:hover': { background: '#e8eaf6' } }}>
                                  <Edit fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Delete">
                                <IconButton size="small" onClick={() => handleDelete(ev.id)}
                                  sx={{ color: '#c62828', '&:hover': { background: '#ffebee' } }}>
                                  <Delete fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </Box>
                          </Box>
                        </Box>
                      );
                    })}
                  </Card>
                </Box>
              ));
            })()
          )}
        </Box>
      )}

      {/* ── Add / Edit Dialog ── */}
      <Dialog open={dialog} onClose={() => setDialog(false)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ fontWeight: 700, color: '#1a237e', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Event sx={{ color: '#1a237e' }} />
            {editingId ? 'Edit Event' : 'Add Event'}
          </Box>
          <IconButton size="small" onClick={() => setDialog(false)}><Close /></IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            <TextField fullWidth size="small" label="Title *" value={form.title} onChange={f('title')} />
            <TextField fullWidth size="small" label="Description" value={form.description} onChange={f('description')} multiline rows={2} />
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField fullWidth size="small" label="Start Date *" type="date" value={form.event_date}
                onChange={f('event_date')} InputLabelProps={{ shrink: true }} />
              <TextField fullWidth size="small" label="End Date" type="date" value={form.end_date}
                onChange={f('end_date')} InputLabelProps={{ shrink: true }}
                helperText="Optional, for multi-day events" />
            </Box>
            <TextField select fullWidth size="small" label="Event Type *" value={form.event_type} onChange={f('event_type')}>
              {EVENT_TYPES.map(t => (
                <MenuItem key={t.value} value={t.value}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box sx={{ width: 12, height: 12, borderRadius: '50%', background: t.color }} />
                    {t.label}
                  </Box>
                </MenuItem>
              ))}
            </TextField>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <input type="checkbox" id="is_holiday" checked={!!form.is_holiday}
                onChange={e => setForm(p => ({ ...p, is_holiday: e.target.checked }))} />
              <label htmlFor="is_holiday" style={{ fontSize: 14, cursor: 'pointer' }}>Mark as Holiday (school closed)</label>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2.5, gap: 1 }}>
          <Button onClick={() => setDialog(false)} variant="outlined" sx={{ borderRadius: 2 }}>Cancel</Button>
          <Button onClick={handleSave} variant="contained" disabled={saving}
            sx={{ background: 'linear-gradient(135deg,#1a237e,#1565c0)', borderRadius: 2, fontWeight: 700 }}>
            {saving ? <CircularProgress size={20} color="inherit" /> : (editingId ? 'Update' : 'Add Event')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
